import {
  Bot,
  CalendarCheck,
  CheckCircle2,
  MonitorSmartphone,
  PhoneMissed,
  ShieldCheck,
  Star,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";

const SERVICES = [
  {
    id: "website",
    icon: MonitorSmartphone,
    title: "Website",
    tagline: "A mobile-first website, built to win local jobs.",
    problem:
      "Most home service sites are slow, hard to read on a phone, and bury the two things customers want: proof you're legit and a way to reach you.",
    includes: [
      "Mobile-first 1–5 page site",
      "Click-to-call on every page",
      "Quote request form",
      "Service-area pages for your towns",
      "Local SEO setup",
      "Google Business Profile cleanup",
    ],
  },
  {
    id: "missed-call",
    icon: PhoneMissed,
    title: "Missed-Call Text-Back",
    tagline: "An automatic text within seconds of any missed call.",
    problem:
      "When you're on a ladder or under a sink, the call goes to voicemail. Plenty of people just dial the next company on the list.",
    includes: [
      "Automatic text within seconds of a missed call",
      "Your greeting, written in plain English",
      "A dedicated, carrier-registered business line",
      "Booking link right in the text",
    ],
  },
  {
    id: "pipeline",
    icon: CalendarCheck,
    title: "Lead Pipeline and Online Booking",
    tagline: "Every lead lands in one pipeline. Customers book a time themselves.",
    problem:
      "Leads scattered across voicemail, texts, and sticky notes are the ones that slip through the cracks.",
    includes: [
      "One pipeline for every lead",
      "Self-serve online booking",
      "Automatic appointment reminders",
    ],
  },
  {
    id: "reviews",
    icon: Star,
    title: "Google Review Requests",
    tagline:
      "After each completed job, every customer gets a polite text or email with a direct review link.",
    problem:
      "Happy customers mean well, but they forget. Reviews only happen when asking is automatic.",
    includes: [
      "Automatic request after each completed job",
      "Direct link to your Google review form",
      "Polite, plain-English wording",
      "Sent to every customer, no exceptions",
    ],
    note: "Every customer gets the same request. We don't filter or screen out unhappy customers — that violates Google's policies and we won't do it.",
  },
  {
    id: "ai-chat",
    icon: Bot,
    title: "AI Chat and Follow-up",
    badge: "Optional add-on",
    tagline: "Answers common questions and collects contact details around the clock.",
    problem:
      "Questions and quote requests arrive at 9pm, not 9am. An unanswered question is a lost job.",
    includes: [
      "Answers common questions at any hour",
      "Collects name, number, and what they need",
      "Follows up so leads don't go cold",
      "Hands real conversations to you",
    ],
  },
  {
    id: "care-plan",
    icon: ShieldCheck,
    title: "Monthly Care Plan",
    tagline: "Hosting, updates, small edits, automation monitoring.",
    problem:
      "Websites and automations need looking after. When nobody's watching, things quietly break.",
    includes: [
      "Hosting and updates handled",
      "Small edits — you text me changes",
      "Automation monitoring",
      "A monthly summary of your leads",
    ],
  },
];

export default function Services() {
  return (
    <>
      <Seo
        title="Contractor Websites, Missed-Call Text-Back and Review Automation | Web Pilot"
        description="Contractor websites, missed-call text-back, online booking, Google review requests, AI chat, and monthly care plans. Web Pilot serves the Indianapolis area."
      />

      <section className="bg-navy py-16 sm:py-20" data-testid="services-hero">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
              Services
            </p>
            <h1
              data-testid="services-heading"
              className="mt-3 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Websites and automation for home service businesses.
            </h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
              Everything here exists for one reason: turning the people who find
              you into the customers who book you.
            </p>
          </Reveal>
        </div>
      </section>

      {SERVICES.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`${i % 2 === 0 ? "bg-offwhite" : "bg-white"} py-16 sm:py-20`}
          data-testid={`service-section-${s.id}`}
        >
          <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-skyblue-soft">
                  <s.icon className="h-6 w-6 text-skyblue" aria-hidden="true" />
                </div>
                {s.badge && (
                  <span
                    data-testid={`service-badge-${s.id}`}
                    className="rounded-full border border-skyblue/40 bg-skyblue-soft px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-skyblue"
                  >
                    {s.badge}
                  </span>
                )}
              </div>
              <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                {s.title}
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                {s.tagline}
              </p>
              <div className="mt-6">
                <p className="text-sm font-bold uppercase tracking-[0.14em] text-slate-400">
                  The problem it fixes
                </p>
                <p className="mt-2 leading-relaxed text-slate-600">{s.problem}</p>
              </div>
              {s.note && (
                <div
                  data-testid={`service-note-${s.id}`}
                  className="mt-6 rounded-lg border-l-4 border-skyblue bg-skyblue-soft p-4 text-sm font-medium leading-relaxed text-navy"
                >
                  {s.note}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.1} className="h-full">
              <div className="h-full rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
                <p className="text-sm font-bold uppercase tracking-[0.14em] text-slate-400">
                  What's included
                </p>
                <ul className="mt-5 space-y-3.5">
                  {s.includes.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 leading-relaxed text-slate-600"
                    >
                      <CheckCircle2
                        className="mt-0.5 h-5 w-5 shrink-0 text-skyblue"
                        aria-hidden="true"
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>
      ))}

      <CtaBand />
    </>
  );
}
